import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useWhatsApp } from '../context/WhatsAppContext';
import {
  MessageCircle,
  Menu,
  X,
  LayoutDashboard,
  MessageSquare,
  Send,
  Users,
  FileText,
  QrCode,
  BarChart3,
  ChevronDown,
  MonitorSmartphone,
  Power,
} from 'lucide-react';
import axios from 'axios';

const Navbar = () => {
  const { status } = useWhatsApp();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Track scroll for shadow effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout? This will clear the current WhatsApp session.')) return;
    setLoggingOut(true);
    try {
      await axios.post('http://localhost:5001/reset-session');
    } catch (err) {
      alert('Logout failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoggingOut(false);
      setShowUserMenu(false);
    }
  };

  const navItems = [
    { label: 'Dashboard', to: '/', icon: LayoutDashboard },
    { label: 'Chat', to: '/chat', icon: MessageSquare },
    { label: 'Send Message', to: '/send-message', icon: Send },
    { label: 'Groups', to: '/groups', icon: Users },
    { label: 'API Docs', to: '/api-docs', icon: FileText },
    { label: 'QR / Link', to: '/generate-qr', icon: QrCode },
    { label: 'Reports', to: '/reports', icon: BarChart3 },
  ];

  const statusConfig = {
    connected: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      dot: 'bg-emerald-500',
      border: 'border-emerald-200',
      label: 'Connected',
    },
    scan_qr: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      dot: 'bg-amber-500',
      border: 'border-amber-200',
      label: 'Scan QR',
    },
    disconnected: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      dot: 'bg-red-500',
      border: 'border-red-200',
      label: 'Disconnected',
    },
    logged_out: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      dot: 'bg-red-500',
      border: 'border-red-200',
      label: 'Logged Out',
    },
    connecting: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      dot: 'bg-blue-500',
      border: 'border-blue-200',
      label: 'Connecting',
    },
  };

  const currentStatus = statusConfig[status] || statusConfig.disconnected;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-gray-200/50'
            : 'bg-white shadow-sm border-b border-gray-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-lg blur-sm opacity-60 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 p-2 rounded-lg shadow-sm">
                  <MessageCircle size={22} className="text-white" />
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-gray-800 tracking-tight">
                  WA Manager
                </span>
                <span className="block text-[10px] text-gray-400 font-medium -mt-0.5">
                  WhatsApp API
                </span>
              </div>
            </NavLink>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || 
                  (item.to !== '/' && location.pathname.startsWith(item.to));
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={`
                      relative flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl
                      transition-all duration-200 group
                      ${isActive
                        ? 'text-emerald-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    {isActive && (
                      <span className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200" />
                    )}
                    <Icon size={16} className={`relative z-10 transition-transform duration-200 ${isActive ? 'text-emerald-500' : 'group-hover:scale-110'}`} />
                    <span className="relative z-10">{item.label}</span>
                    {/* {isActive && (
                      <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-400 rounded-full shadow-sm" />
                    )} */}
                  </NavLink>
                );
              })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Status Badge */}
              <div
                className={`
                  hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold
                  transition-all duration-300 ${currentStatus.border} ${currentStatus.bg} ${currentStatus.text}
                  shadow-sm
                `}
              >
                <span className="relative flex h-2 w-2">
                  <span
                    className={`animate-ping absolute inline-flex h-full w-full rounded-full ${currentStatus.dot} opacity-75`}
                  />
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${currentStatus.dot}`} />
                </span>
                <span className="uppercase tracking-wider">
                  {currentStatus.label}
                </span>
              </div>

              {/* User Menu / Logout */}
              {status === 'connected' && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all duration-200"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-sm">
                      <MonitorSmartphone size={14} className="text-white" />
                    </div>
                    <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 py-2 bg-white/95 backdrop-blur-xl border border-gray-200 rounded-2xl shadow-xl shadow-gray-200/50 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-400">WhatsApp Session</p>
                        <p className="text-sm text-gray-800 font-medium flex items-center gap-1.5 mt-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Active
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors mt-1"
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                          <Power size={16} />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{loggingOut ? 'Logging out...' : 'Logout'}</p>
                          <p className="text-xs text-gray-400">Clear session & disconnect</p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile menu button */}
              <button
                className="lg:hidden relative w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all duration-200"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X size={20} className="text-gray-600" />
                ) : (
                  <Menu size={20} className="text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <div
          className={`
            lg:hidden overflow-hidden transition-all duration-300 ease-in-out
            ${mobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="bg-white/95 backdrop-blur-xl border-t border-gray-100 px-4 py-3 space-y-1 shadow-inner">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to ||
                (item.to !== '/' && location.pathname.startsWith(item.to));
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={`
                    flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200
                    ${isActive
                      ? 'text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon size={18} className={isActive ? 'text-emerald-500' : 'text-gray-400'} />
                  <span>{item.label}</span>
                  {/* {isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm" />
                  )} */}
                </NavLink>
              );
            })}

            {/* Mobile divider */}
            <div className="border-t border-gray-100 my-2" />

            {/* Mobile status */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${currentStatus.border} ${currentStatus.bg} ${currentStatus.text}`}>
                <span className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${currentStatus.dot} opacity-75`} />
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${currentStatus.dot}`} />
                </span>
                <span className="uppercase tracking-wider">{currentStatus.label}</span>
              </div>
            </div>

            {/* Mobile logout */}
            {status === 'connected' && (
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
              >
                <Power size={18} />
                <span>{loggingOut ? 'Logging out...' : 'Logout WhatsApp'}</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-16" />
    </>
  );
};

export default Navbar;